import { FC, PropsWithChildren, ReactNode, useEffect, useMemo, useState } from 'react'
import { Button, Layout as AntLayout, Typography } from 'antd'
import { MenuOutlined, LeftOutlined } from '@ant-design/icons'
import { useQueryClient } from 'react-query'
import Menu from '../Menu'
import Styled from './styled'
import Container from 'components/Container'
import { useLocalization } from 'localization'
import http, { CLIENT_ID_KEY } from 'api/http'
import appRoutes from '../app-routes'
import { useScreenMatch } from 'theme/hooks'
import { useCurrentUser } from 'api'
import Link from 'next/link'
import { useAppContext } from '../appContext'
import DrawerProvider from './context'

const COLLAPSED_KEY = 'menu.collapsed'

interface ITrigger {
	collapsed: boolean
}

const Trigger: FC<ITrigger> = ({ collapsed }) => (collapsed ? <MenuOutlined /> : <LeftOutlined />)

interface ILayout extends PropsWithChildren {
	title: string
	extra?: ReactNode
}

const Layout: FC<ILayout> = ({ children, title, extra }) => {
	const { t } = useLocalization()
	const [collapsed, setCollapsed] = useState(
		(typeof localStorage !== 'undefined' && localStorage.getItem(COLLAPSED_KEY)) === 'true'
	)
	const queryClient = useQueryClient()
	const { data: user, cpaClientView, cpaOwnView } = useCurrentUser()
	const match = useScreenMatch()
	const [open, setOpen] = useState(false)
	const { isWealthicaContext } = useAppContext()

	useEffect(() => {
		localStorage.setItem(COLLAPSED_KEY, collapsed.toString())
	}, [collapsed])

	const onChangeClient = () => {
		http.forgetStorageItem(CLIENT_ID_KEY)
		window.location.href = appRoutes.clients.list()
		queryClient.invalidateQueries()
	}

	const contextValue = useMemo(() => ({ isOpen: !collapsed }), [collapsed])

	return (
		<AntLayout>
			{user?.email && (cpaClientView || cpaOwnView) && (
				<Styled.ClientHeader>
					<Typography.Text>{t('clients.viewClientAlert.title', { email: user?.email })}</Typography.Text>

					<Button type="link" danger onClick={onChangeClient}>
						{t('actions.changeClient')}
					</Button>
				</Styled.ClientHeader>
			)}
			<Styled.Layout $isIframe={isWealthicaContext}>
				{match.gte('xl') ? (
					<AntLayout.Sider
						collapsible
						collapsed={collapsed}
						onCollapse={() => setCollapsed(!collapsed)}
						width={245}
						collapsedWidth={match.gte('xl') ? 80 : 0}
						trigger={null}
					>
						<Styled.SiderHeader display="inline-flex" alignItems="center" padding="sm" $collapsed={collapsed}>
							{!collapsed && (
								<Link href={appRoutes.dashboard()} passHref>
									<a>
										<img src="/logo.svg" alt="Logo" />
									</a>
								</Link>
							)}
							<Button
								type="text"
								icon={<Trigger collapsed={collapsed} />}
								onClick={() => setCollapsed(!collapsed)}
							></Button>
							{!collapsed && user?.referral && user.referral !== 'no_referral' && (
								<Styled.Referral space="xxs">
									<Typography.Text>{t('dashboard.referralText')}</Typography.Text>
									{user.referral === 'coinomi' && <img src="/integrations/coinomi.png" alt="coinomi" />}
								</Styled.Referral>
							)}
						</Styled.SiderHeader>
						<Menu />
					</AntLayout.Sider>
				) : (
					<Styled.MobileMenu
						title={
							<Link href={appRoutes.dashboard()} passHref>
								<a>
									<img src="/logo.svg" alt="Logo" />
								</a>
							</Link>
						}
						placement="left"
						onClose={() => setOpen(false)}
						width={220}
						open={open}
						closeIcon={<LeftOutlined />}
					>
						<Menu onClick={() => setOpen(false)} />
					</Styled.MobileMenu>
				)}
				<AntLayout.Content>
					<Styled.ContentLayout>
						<AntLayout.Header>
							<Container display="flex" alignItems="center" space="xs" flex={1}>
								{!match.gte('xl') && (
									<Button type="text" icon={<MenuOutlined />} onClick={() => setOpen(!open)}></Button>
								)}
								<Typography.Title level={3}>{title}</Typography.Title>
								{extra && (
									<Container display="flex" alignItems="center" justifyContent="flex-end" width="100%">
										{extra}
									</Container>
								)}
							</Container>
						</AntLayout.Header>
						<AntLayout.Content>
							<DrawerProvider value={contextValue}>{children}</DrawerProvider>
						</AntLayout.Content>
					</Styled.ContentLayout>
				</AntLayout.Content>
			</Styled.Layout>
		</AntLayout>
	)
}

export default Layout
